import React from "react";
import i18n from "i18next";
import styled from "styled-components";
import { connect, useStore } from "react-redux";
import { DialogOverlay, Dialog } from "components/dialog/DialogOverlay";
import {
  getTickerUsingId,
  searchPortfolioForText,
  currentPortfolioSelector,
  getTickerUsingShortName,
  getCustodianItemsSelector,
  getMonthAndYearFromDate,
  parseKuberaDateString,
  PVST_VALUE_TICKER_ID,
  sheetSectionsSelector,
  custodianSelector,
  accountLinkingService,
  isFiatManualCustodian
} from "@kubera/common";
import { addKeyboardEventListener, removeKeyboardEventListener } from "utilities/EventManager";
import { category } from "components/dashboard/DashboardComponentExports";
import CurrencyLabel from "components/labels/CurrencyLabel";
import SearchInput from "components/inputs/SearchInput";
import LinkedIcon from "assets/images/linked_account_icon.svg?react";

export const custodianPickerMode = { ACCOUNT: "account", HOLDING: "holding" };

const PickerDialog = styled(Dialog)`
  position: relative;
  width: 870px;
  min-height: 700px;
  display: flex;
  align-items: stretch;
  margin-top: 74px;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  margin: 60px 60px 60px 60px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: left;
  flex: 1;
`;

const SubTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
  line-height: 15.73px;
  letter-spacing: 0%;
  margin-bottom: 8px;
`;

const Title = styled.div`
  font-style: normal;
  font-weight: 700;
  font-size: 22px;
  line-height: 130%;
  font-feature-settings: "ss01" on;
  color: #000000;
`;

const Category = styled.span`
  margin-left: 5px;
  margin-right: 5px;
  text-decoration: ${props => props.editable === true && "underline"};
  text-underline-offset: 4px;
  cursor: ${props => props.editable === true && "pointer"};
`;

const Description = styled.div`
  opacity: 0.6;
  margin-top: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19.6px;
`;

const SearchBox = styled(SearchInput)`
  height: 45px;
  margin-top: 12px;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  font-feature-settings: "ss01" on;
  line-height: 17px;
  background-color: #ffffff;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1px;
  box-sizing: border-box;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  background: #ffffff;
`;

const Result = styled.div`
  display: flex;
  padding: 12px 18px 12px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: ${props => (props.isFocused === true ? props.theme.focusBackgroundColor : "#ffffff")};
  cursor: pointer;
`;

const EmptyResult = styled(Result)`
  cursor: auto;
  font-size: 14px;
  padding-left: 10px;
`;

const ResultDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ResultNameContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ResultName = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  font-feature-settings: "ss01" on, "calt" off;
  color: #000000;
`;

const ResultMeta = styled.div`
  margin-left: 5px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const LinkedCustodianIcon = styled(LinkedIcon)`
  path {
    fill: ${props => props.theme.mobileTxtColorCustodianDetailsLinkedIcon};
  }
  width: 12px;
  height: 12px;
  margin-left: 5px;
`;

const ResultDescription = styled.div`
  margin-top: 3px;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const ResultPath = styled.div`
  width: fit-content;
  margin-top: 3px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const ResultValueContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const ResultValue = styled(CurrencyLabel)`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  text-align: right;
  font-feature-settings: "ss01" on, "calt" off;
  color: #000000;
`;

const ResultValueOriginalCurrency = styled(CurrencyLabel)`
  margin-top: 3px;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  font-feature-settings: "ss01" on;
  color: rgba(0, 0, 0, 0.5);
`;

const NewCustodianHeader = styled.div`
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;
  text-transform: uppercase;
  margin-top: 18px;
  margin-left: 1px;
`;

const NewAssetRow = styled.div`
  background-color: white;
  height: 59px;
  padding-left: 17px;
  font-size: 14px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-top: ${props => (props.isFirst ? "5px" : "-1px")};
  cursor: pointer;
`;

const SuggestionsContainer = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
`;

const SuggestionTitle = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0%;
  color: #00000099;
  white-space: pre-wrap;
`;

const SuggestionList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  border: 1px solid #0000001a;
`;

const Suggestion = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 16.94px;
  letter-spacing: 0%;
  padding: 12px 20px 12px 20px;
  border-top: ${props => (props.isFirst ? "none" : "1px solid #0000001a")};
  cursor: pointer;
`;

const OrDivider = styled.div`
  margin-top: 30px;
  margin-bottom: 30px;
  font-weight: 700;
  font-size: 12px;
  line-height: normal;
  color: rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  font-feature-settings: "ss01" on, "calt" off;
`;

const SplitSectionTitle = styled(Title)`
  margin-bottom: 4px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;

const HeaderTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ToggleModeLink = styled.div`
  margin-left: 20px;
  margin-bottom: 2px;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  color: #0074fc;
  text-decoration: underline;
  text-underline-offset: 1px;
  white-space: nowrap;
  cursor: pointer;
`;

const AccountSearchRow = styled(SearchInput)`
  border: none;
  border-top: ${props => (props.isFirst === true ? "none" : "1px solid #0000001a")};
`;

class CustodiansPickerDialog extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchText: "",
      searchResults: null,
      accountSearchText: "",
      accountSearchResults: null,
      currentlySelectedResultIndex: null,
      selectedItems: this.getDefaultItems(),
      selectedCategory: props.category,
      suggestions: this.getSuggestions(props.category, props.suggestions),
      pickerMode:
        props.allowHoldingMode === true && props.isHolding === true
          ? custodianPickerMode.HOLDING
          : custodianPickerMode.ACCOUNT
    };

    this.handleSearchInputChange = this.handleSearchInputChange.bind(this);
    this.handleAccountSearchInputChange = this.handleAccountSearchInputChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleResultInteraction = this.handleResultInteraction.bind(this);
    this.handleHoldingParentSelection = this.handleHoldingParentSelection.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);
    this.handleCategoryChangeClick = this.handleCategoryChangeClick.bind(this);
    this.handlePickerModeToggle = this.handlePickerModeToggle.bind(this);
  }

  getSuggestions(category, defaultSuggestions) {
    if (!this.props.showSuggestions === true) {
      return [];
    }

    const provided = defaultSuggestions ? defaultSuggestions[category] || [] : [];

    if (this.props.splitView !== true) {
      return defaultSuggestions ? provided : this.getAllLocationSuggestions(category);
    }

    return [...provided, ...this.getAllLocationSuggestions(category)];
  }

  getAllLocationSuggestions(category) {
    const splitView = this.props.splitView === true;
    let suggestions = [];
    for (const sheet of this.props.currentPortfolio.details.sheet) {
      if (sheet.category !== category) {
        continue;
      }

      const sections = sheetSectionsSelector(this.props.store.getState(), this.props.currentPortfolio.id, sheet.id);
      const isMultiSection = sections.length > 1;

      if (!(splitView && isMultiSection)) {
        suggestions.push({
          isSheet: true,
          ...sheet
        });
      }
      if (!isMultiSection) {
        continue;
      }
      for (const section of sections) {
        suggestions.push({
          isSection: true,
          sheet: sheet,
          ...section
        });
      }
    }
    return suggestions;
  }

  getDefaultItems() {
    if (!this.props.data === true || !this.props.data.items === true) {
      return [];
    }
    return this.props.custodianItems;
  }

  componentDidMount() {
    addKeyboardEventListener(this.handleKeyDown);
  }

  componentWillUnmount() {
    removeKeyboardEventListener(this.handleKeyDown, true);
  }

  handleKeyDown(e) {
    if (e.key === "ArrowUp") {
      this.selectPreviousResult();
      e.preventDefault();
      return true;
    } else if (e.key === "ArrowDown") {
      this.selectNextResult();
      e.preventDefault();
      return true;
    } else if (e.key === "Enter") {
      if (!this.state.searchResults || this.state.searchResults.length === 0) {
        if (this.state.selectedItems?.length) {
          this.handleSaveClick(e);
        }
      } else if (this.state.currentlySelectedResultIndex !== null) {
        const result = this.state.searchResults[this.state.currentlySelectedResultIndex];
        this.handleResultInteraction(result);
      }
      e.preventDefault();
      return true;
    }
    return false;
  }

  handleSearchInputChange(searchText) {
    this.setState({ searchText: searchText });

    if (!this.searchTimer === false) {
      clearTimeout(this.searchTimer);
    }

    if (!searchText === true) {
      this.setState({ searchText: searchText, searchResults: null });
      return;
    } else {
      this.setState({ searchText: searchText });
    }

    this.searchTimer = setTimeout(() => {
      this.props.searchPortfolioForText(searchText, false, this.state.selectedCategory, true, data => {
        if (data.searchText !== searchText || this.state.searchText !== searchText) {
          return;
        }
        let results;
        if (this.state.pickerMode === custodianPickerMode.HOLDING) {
          results = data.results.filter(item => this.isManualHolding(item));
        } else {
          results =
            this.onlyCustodians() === true ? data.results.filter(item => item.isCustodian === true) : data.results;
          if (this.props.filterResults) {
            results = results.filter(item => this.props.filterResults(item));
          }
        }
        this.setState({ searchResults: results });
      });
      this.searchTimer = null;
    }, 300);
  }

  handleAccountSearchInputChange(searchText) {
    this.setState({ accountSearchText: searchText });

    if (this.accountSearchTimer) {
      clearTimeout(this.accountSearchTimer);
    }

    if (!searchText) {
      this.setState({ accountSearchResults: null });
      return;
    }

    this.accountSearchTimer = setTimeout(() => {
      this.props.searchPortfolioForText(searchText, false, category.ASSET, true, data => {
        if (data.searchText !== searchText || this.state.accountSearchText !== searchText) {
          return;
        }
        this.setState({ accountSearchResults: data.results.filter(item => this.isManualAccount(item)) });
      });
      this.accountSearchTimer = null;
    }, 300);
  }

  isManualHolding(item) {
    if (item.isCustodian !== true || !item.parentId) {
      return false;
    }
    const parent = custodianSelector(this.props.store.getState(), item.parentId);
    return parent?.linkType === accountLinkingService.MANUAL_CONNECTED;
  }

  isManualAccount(item) {
    if (item.isCustodian !== true) {
      return false;
    }
    const state = this.props.store.getState();
    return isFiatManualCustodian(state, custodianSelector(state, item.id));
  }

  handlePickerModeToggle() {
    const nextMode =
      this.state.pickerMode === custodianPickerMode.HOLDING ? custodianPickerMode.ACCOUNT : custodianPickerMode.HOLDING;
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (this.accountSearchTimer) {
      clearTimeout(this.accountSearchTimer);
    }
    this.setState({
      pickerMode: nextMode,
      searchText: "",
      searchResults: null,
      accountSearchText: "",
      accountSearchResults: null,
      currentlySelectedResultIndex: null
    });
  }

  handleHoldingParentSelection(location) {
    this.props.onVariableUpdate({ ...(this.props.data || {}), items: [location] });
    this.props.onDismiss();
  }

  handleResultInteraction(result) {
    if (!this.state.selectedItems.find(item => item.id === result.id) === true) {
      var selectedItems = this.props.allowMultiple === true ? this.state.selectedItems : [];
      selectedItems.push(result);
      this.setState({ selectedItems: selectedItems }, this.handleSaveClick);
    }

    this.handleSearchInputChange("");
  }

  handleSaveClick(e) {
    const items = this.state.selectedItems
      .filter(item => !!item)
      .map(item => {
        if (!this.props.returnResultWithDetails === false) {
          return item;
        }

        const entry = { id: item.id };
        if (item.isNewCustodian === true) {
          entry.isNewCustodian = true;
          entry.isCustodian = true;
        } else if (item.isCustodian === true) {
          entry.isCustodian = true;
          entry.sectionId = item.sectionId;
          entry.sheetId = item.sheet.id;
          entry.valueTickerId = item.valueTickerId;
        } else if (item.isSection === true) {
          entry.isSection = true;
          entry.sheetId = item.sheetId;
          entry.sectionId = item.id;
        } else if (item.isSheet === true) {
          entry.isSheet = true;
        }
        return entry;
      });

    this.props.onVariableUpdate({ ...(this.props.data || {}), items: items });
    this.props.onDismiss();
  }

  handleCategoryChangeClick(e) {
    if (this.props.allowCategoryChange === true) {
      const newCategory = this.state.selectedCategory === category.ASSET ? category.DEBT : category.ASSET;
      this.setState({
        selectedCategory: newCategory,
        suggestions: this.getSuggestions(newCategory, this.props.suggestions)
      });
      this.handleSearchInputChange("");
    }
  }

  getResultPath(result) {
    if (result.isCustodian === true && result.sheet && result.section) {
      if (!result.parentId === false && result.hidden === 1 && result.parent) {
        return `${this.getCategoryNameForCategory(result.sheet.category)} / ${result.sheet.name} / ${
          result.section.name
        } / ${result.parent.name}`;
      } else {
        return `${this.getCategoryNameForCategory(result.sheet.category)} / ${result.sheet.name} / ${
          result.section.name
        }`;
      }
    } else if (result.isSection === true && result.sheet) {
      return `${this.getCategoryNameForCategory(result.sheet.category)} / ${result.sheet.name}`;
    } else if (result.isSheet === true && result.category) {
      return `${this.getCategoryNameForCategory(result.category)}`;
    }
    return "";
  }

  getSuggestionPath(suggestion) {
    let path = "";
    if (suggestion.isParent === true && suggestion.sheet && suggestion.section) {
      const isSingleSectionSheet =
        !suggestion.createNewSection === true &&
        sheetSectionsSelector(this.props.store.getState(), this.props.currentPortfolio.id, suggestion.sheet.id)
          .length === 1;
      const basePath = isSingleSectionSheet
        ? suggestion.sheet.name
        : `${suggestion.sheet.name} / ${suggestion.section.name}`;
      const parentLabel =
        suggestion.isNewParent === true
          ? `${suggestion.parentCustodianName} ${i18n.t("aiImport.newAccountSuffix")}`
          : suggestion.parentCustodianName;
      return `${basePath} / ${parentLabel}`;
    }
    if (suggestion.isSection) {
      path = `${suggestion.sheet.name} / ${suggestion.name}`;
    } else if (suggestion.isSheet) {
      path = `${suggestion.name}`;
    } else if (suggestion.isCustodian) {
      const isSingleSectionSheet =
        sheetSectionsSelector(this.props.store.getState(), this.props.currentPortfolio.id, suggestion.sheet.id)
          .length === 1;
      path = isSingleSectionSheet
        ? `${suggestion.sheet.name} / ${suggestion.name}`
        : `${suggestion.sheet.name} / ${suggestion.section.name} / ${suggestion.name}`;
    }
    if (path && suggestion.createNewSection === true) {
      path = `${path} ${i18n.t("aiImport.newSectionSuffix")}`;
    }
    return path;
  }

  getCategoryNameForCategory(categoryName) {
    if (categoryName === category.ASSET) {
      return i18n.t("assets");
    } else if (categoryName === category.DEBT) {
      return i18n.t("debts");
    } else if (categoryName === category.INSURANCE) {
      return i18n.t("insurance");
    }
    return null;
  }

  selectNextResult() {
    const currentIndex = this.state.currentlySelectedResultIndex;

    if (!this.state.searchResults || this.state.searchResults.length === 0) {
      return;
    }

    if (currentIndex === this.state.searchResults.length - 1) {
      return;
    }

    this.setState({ currentlySelectedResultIndex: currentIndex === null ? 0 : currentIndex + 1 });
  }

  selectPreviousResult() {
    const currentIndex = this.state.currentlySelectedResultIndex;
    if (currentIndex === null || currentIndex === 0) {
      return;
    }
    this.setState({ currentlySelectedResultIndex: currentIndex - 1 });
  }

  selectResult(index) {
    this.setState({ currentlySelectedResultIndex: index });
  }

  onlyCustodians() {
    return (
      !this.props.data === false && !this.props.data.props === false && this.props.data.props.onlyCustodians === true
    );
  }

  renderToggleLink() {
    if (this.props.allowHoldingMode !== true) {
      return null;
    }
    const isHoldingMode = this.state.pickerMode === custodianPickerMode.HOLDING;
    if (!isHoldingMode && this.state.selectedCategory !== category.ASSET) {
      return null;
    }
    return (
      <ToggleModeLink onClick={this.handlePickerModeToggle}>
        {isHoldingMode ? i18n.t("aiImport.markAsAccount") : i18n.t("aiImport.markAsHolding")}
      </ToggleModeLink>
    );
  }

  renderTopSearchResults() {
    if (!this.state.searchResults) {
      return null;
    }
    const portfolioTicker = this.props.portfolioTicker;
    return (
      <ResultsContainer>
        {this.state.searchResults.map((result, index) => (
          <div key={index}>
            <Result
              isFocused={this.state.currentlySelectedResultIndex === index}
              onClick={e => this.handleResultInteraction(result)}
              onMouseEnter={() => {
                this.selectResult(index);
              }}
            >
              <ResultDetailsContainer>
                <ResultNameContainer>
                  <ResultName>{result.name}</ResultName>
                  {!result.linkType === false && <LinkedCustodianIcon />}
                  {result.isSection === true && <ResultMeta>{"(Section)"}</ResultMeta>}
                  {result.isSheet === true && <ResultMeta>{"(Sheet)"}</ResultMeta>}
                </ResultNameContainer>
                {!result.description === false && <ResultDescription>{result.description}</ResultDescription>}
                <ResultPath>{this.getResultPath(result)}</ResultPath>
              </ResultDetailsContainer>
              <ResultValueContainer>
                <ResultValue
                  currency={this.props.currentPortfolio.currency}
                  value={result.total}
                  abbreviate={false}
                  roundDown={true}
                />
                {result.isCustodian === true &&
                  portfolioTicker.id !== result.valueTickerId &&
                  result.valueTickerId !== PVST_VALUE_TICKER_ID && (
                    <ResultValueOriginalCurrency
                      currency={getTickerUsingId(this.props.store.getState(), result.valueTickerId).shortName}
                      value={result.value}
                      abbreviate={false}
                      roundDown={true}
                      showCurrencyAsSymbol={false}
                    />
                  )}
              </ResultValueContainer>
            </Result>
          </div>
        ))}
        {this.state.searchResults.length === 0 && <EmptyResult>{i18n.t("assetNotFound")}</EmptyResult>}
      </ResultsContainer>
    );
  }

  renderAccountSearchResults() {
    const newAccountName = this.state.accountSearchText.trim();
    const results = this.state.accountSearchResults || [];
    if (results.length === 0 && newAccountName.length === 0) {
      return null;
    }
    return (
      <ResultsContainer>
        {results.map((result, index) => (
          <Result
            key={index}
            onClick={e =>
              this.handleHoldingParentSelection({
                isParent: true,
                parentCustodianId: result.id,
                parentCustodianName: result.name,
                section: result.section,
                sheet: result.sheet
              })
            }
          >
            <ResultDetailsContainer>
              <ResultNameContainer>
                <ResultName>{result.name}</ResultName>
                {!result.linkType === false && <LinkedCustodianIcon />}
              </ResultNameContainer>
              <ResultPath>{this.getResultPath(result)}</ResultPath>
            </ResultDetailsContainer>
          </Result>
        ))}
        {newAccountName.length > 0 && (
          <Result
            onClick={e =>
              this.handleHoldingParentSelection({
                isParent: true,
                isNewParent: true,
                isNewAccountRequest: true,
                parentCustodianName: newAccountName
              })
            }
          >
            <ResultDetailsContainer>
              <ResultNameContainer>
                <ResultName>{`${newAccountName} ${i18n.t("aiImport.newAccountSuffix")}`}</ResultName>
              </ResultNameContainer>
            </ResultDetailsContainer>
          </Result>
        )}
      </ResultsContainer>
    );
  }

  renderHoldingContent() {
    const suggestions = this.props.holdingAccountSuggestions || [];
    return (
      <>
        <HeaderRow>
          <HeaderTextContainer>
            <Title>{i18n.t("aiImport.selectHoldingTitle")}</Title>
            <Description>{i18n.t("aiImport.selectHoldingDesc")}</Description>
          </HeaderTextContainer>
          {this.renderToggleLink()}
        </HeaderRow>
        <SearchBox
          autoFocus={true}
          clearOnEscape={true}
          value={this.state.searchText}
          onChange={this.handleSearchInputChange}
          placeholder={i18n.t("aiImport.searchManual").replace("%s1%", i18n.t("holdings"))}
        />
        {this.renderTopSearchResults()}
        <SuggestionsContainer>
          <OrDivider>{`—— ${i18n.t("or")} ——`}</OrDivider>
          <SplitSectionTitle>{i18n.t("aiImport.addToAccount")}</SplitSectionTitle>
          <Description>{i18n.t("aiImport.addToAccountDesc")}</Description>
          <SuggestionList>
            {suggestions.map((suggestion, index) => (
              <Suggestion
                key={index}
                isFirst={index === 0}
                onClick={e => this.handleHoldingParentSelection(suggestion.location)}
              >
                {suggestion.isNew === true
                  ? `${suggestion.name} ${i18n.t("aiImport.newAccountSuffix")}`
                  : suggestion.name}
              </Suggestion>
            ))}
            <AccountSearchRow
              clearOnEscape={true}
              isFirst={suggestions.length === 0}
              value={this.state.accountSearchText}
              onChange={this.handleAccountSearchInputChange}
              placeholder={i18n.t("aiImport.searchOrCreateAccount")}
            />
          </SuggestionList>
          {this.renderAccountSearchResults()}
        </SuggestionsContainer>
      </>
    );
  }

  renderAccountTitleAndDescription() {
    return (
      <>
        <Title>
          {this.onlyCustodians() === true ? (
            i18n
              .t("custodiansOnlyPickerTitle")
              .replace("%s1%", this.getCategoryNameForCategory(this.state.selectedCategory).slice(0, -1))
          ) : (
            <>
              <span>{i18n.t("select")}</span>
              <Category editable={this.props.allowCategoryChange} onClick={this.handleCategoryChangeClick}>
                {this.getCategoryNameForCategory(this.state.selectedCategory).slice(0, -1)}
              </Category>
              {this.props.isLocation && this.props.splitView !== true && (
                <span>{i18n.t("custodiansPickerLocation")}</span>
              )}
            </>
          )}
        </Title>
        {this.props.assetDescription && this.props.debtDescription && (
          <Description>
            {this.state.selectedCategory === category.ASSET ? this.props.assetDescription : this.props.debtDescription}
          </Description>
        )}
      </>
    );
  }

  renderAccountContent() {
    return (
      <>
        {this.props.allowHoldingMode === true ? (
          <HeaderRow>
            <HeaderTextContainer>{this.renderAccountTitleAndDescription()}</HeaderTextContainer>
            {this.renderToggleLink()}
          </HeaderRow>
        ) : (
          this.renderAccountTitleAndDescription()
        )}
        <SearchBox
          autoFocus={true}
          clearOnEscape={true}
          value={this.state.searchText}
          onChange={this.handleSearchInputChange}
          placeholder={
            this.props.splitView === true
              ? i18n
                  .t("aiImport.searchManual")
                  .replace("%s1%", this.getCategoryNameForCategory(this.state.selectedCategory).toLocaleLowerCase())
              : this.onlyCustodians() === true
              ? i18n
                  .t("custodiansOnlySearchPlaceholder")
                  .replace("%s1%", this.getCategoryNameForCategory(this.state.selectedCategory))
              : i18n.t(
                  this.state.selectedCategory === category.ASSET
                    ? "custodiansSearchPlaceholderAsset"
                    : "custodiansSearchPlaceholderDebt"
                )
          }
        />
        {this.renderTopSearchResults()}
        {!this.state.searchText && this.props.newCustodians && this.props.newCustodians.length > 0 && (
          <>
            <NewCustodianHeader>
              {i18n.t(this.state.selectedCategory === category.ASSET ? "futureAssets" : "futureDebts")}
            </NewCustodianHeader>
            {this.props.newCustodians.map((result, index) => (
              <div key={result.id}>
                <NewAssetRow
                  onClick={e =>
                    this.handleResultInteraction({ id: result.id, isCustodian: true, isNewCustodian: true })
                  }
                  isFirst={index === 0}
                >
                  <ResultDetailsContainer>
                    <ResultNameContainer>
                      <ResultName>{result.name}</ResultName>
                    </ResultNameContainer>
                    <ResultPath>{`${i18n.t("from")} ${getMonthAndYearFromDate(
                      parseKuberaDateString(result.date)
                    )}`}</ResultPath>
                  </ResultDetailsContainer>
                </NewAssetRow>
              </div>
            ))}
          </>
        )}
        {!this.state.searchResults === true &&
          this.props.showSuggestions === true &&
          this.state.suggestions.length > 0 && (
            <SuggestionsContainer>
              {this.props.splitView === true ? (
                <>
                  <OrDivider>{`—— ${i18n.t("or")} ——`}</OrDivider>
                  <SplitSectionTitle>
                    <span>
                      {i18n
                        .t("addAsNew")
                        .split("%s1%")[0]
                        .trimEnd()}
                    </span>
                    <Category editable={this.props.allowCategoryChange} onClick={this.handleCategoryChangeClick}>
                      {this.getCategoryNameForCategory(this.state.selectedCategory).slice(0, -1)}
                    </Category>
                  </SplitSectionTitle>
                  <Description>
                    {this.state.selectedCategory === category.ASSET
                      ? i18n.t("aiImport.addAsNewAssetDesc")
                      : i18n.t("aiImport.addAsNewDebtDesc")}
                  </Description>
                </>
              ) : (
                <SuggestionTitle>
                  {this.props.suggestionsSubtext?.replace(
                    "%s1%",
                    this.getCategoryNameForCategory(this.state.selectedCategory).toLocaleLowerCase()
                  ) || i18n.t("recent")}
                </SuggestionTitle>
              )}
              <SuggestionList>
                {this.state.suggestions.map((suggestion, index) => (
                  <Suggestion key={index} isFirst={index === 0} onClick={e => this.handleResultInteraction(suggestion)}>
                    {this.getSuggestionPath(suggestion)}
                  </Suggestion>
                ))}
              </SuggestionList>
            </SuggestionsContainer>
          )}
      </>
    );
  }

  render() {
    return (
      <DialogOverlay onDismiss={this.props.onDismiss}>
        <PickerDialog>
          <Container>
            {this.props.subTitle && <SubTitle>{this.props.subTitle}</SubTitle>}
            {this.state.pickerMode === custodianPickerMode.HOLDING
              ? this.renderHoldingContent()
              : this.renderAccountContent()}
          </Container>
        </PickerDialog>
      </DialogOverlay>
    );
  }
}

const mapStateToProps = (state, props) => {
  const currentPortfolio = currentPortfolioSelector(state);
  return {
    currentPortfolio,
    custodianItems: getCustodianItemsSelector(state, props.data?.items),
    portfolioTicker: getTickerUsingShortName(state, currentPortfolio.currency)
  };
};

const mapDispatchToProps = {
  searchPortfolioForText: searchPortfolioForText
};

const CustodiansPickerDialogWrapper = props => {
  const store = useStore();
  return <CustodiansPickerDialog {...props} store={store} />;
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CustodiansPickerDialogWrapper);
